import { IonContent, IonPage, IonModal, IonButton } from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { useClerk } from '@clerk/clerk-react';
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { loginWithSecret, whoAmI, logout } from '../services/authService';
import { setJWT, setActiveProfileId } from '../services/apiService';
import { getActiveProfile } from '../services/profileService';
import { getUnacknowledgedInvitations, acceptReject } from '../services/invitationService';
import { Invitation, InvitationType } from '../types/invitation';
import { ProfileStatus } from '../types/profile';
import { HomeContent } from '@/components/ui/HomeContent';

export const InvitedPage: React.FC = () => {
  const router = useIonRouter();
  const location = useLocation();
  const { signOut, loaded: clerkLoaded, session } = useClerk();
  const [showModal, setShowModal] = useState(false);
  const [teamInvitation, setTeamInvitation] = useState<Invitation | null>(null);
  const [accepted, setAccepted] = useState(false);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const processInvitation = async () => {
      if (hasProcessed.current) return;
      hasProcessed.current = true;

      const searchParams = new URLSearchParams(location.search);
      const profileId = searchParams.get('profileId');
      const activityId = searchParams.get('activityId');
      const secret = searchParams.get('s');

      if (profileId && activityId && secret) {
        try {
          // If user is signed into Clerk, logout and sign them out with redirect back to this same URL
          if (session) {
            const currentUrl = `${window.location.pathname}${window.location.search}`;

            // Logout with Clerk signOut that redirects back to this same URL
            await logout(async () => {
              await signOut({ redirectUrl: currentUrl });
            });
            return; // Exit - Clerk will redirect back to this same route
          }

          // User is not signed into Clerk, proceed with secret login
          const response = await loginWithSecret(secret);
          setJWT(response.jwt);
          setActiveProfileId(response.profileId);

          await whoAmI(profileId);
          const activeProfile = getActiveProfile();

          if (activeProfile?.status === ProfileStatus.Pending) {
            const invitations = await getUnacknowledgedInvitations();
            const teamInv = invitations.find(inv => inv.type === InvitationType.Team);

            if (teamInv) {
              setTeamInvitation(teamInv);
              setShowModal(true);
            } else {
              router.push('/home', 'none');
            }
          } else {
            router.push('/home', 'none');
          }
        } catch (error) {}
      }
    };

    processInvitation();
  }, [location.search, clerkLoaded, session, signOut]);

  const handleAccept = async () => {
    if (teamInvitation) {
      try {
        await acceptReject(teamInvitation.type, teamInvitation.id, 'accept');
        setAccepted(true);
      } catch (error) {
        router.push('/dashboard', 'none');
      }
    }
  };

  const handleContinue = () => {
    setShowModal(false);
    const activeProfile = getActiveProfile();
    const email = activeProfile?.email || '';
    router.push(`/sign-up?email=${encodeURIComponent(email)}`, 'none');
  };

  const handleReject = async () => {
    if (teamInvitation) {
      try {
        await acceptReject(teamInvitation.type, teamInvitation.id, 'reject');
        setShowModal(false);
        router.push('/dashboard', 'none');
      } catch (error) {
        router.push('/dashboard', 'none');
      }
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
      </IonContent>

      <HomeContent />

      <IonModal 
        isOpen={showModal} 
        backdropDismiss={false}
        className="invite-modal"
      >
        <div className="flex flex-col">
          <div className="px-6 pt-6 pb-4 text-center flex flex-col items-center">
            {accepted ? (
              <>
                <p className="text-[#928881] text-base mb-2">Invitation accepted! Click continue to sign up.</p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold mb-4">Welcome to Rev Leasing!</h1>
                <p className="text-lg mb-2">You've been invited to join</p>
                <p className="text-3xl font-semibold text-[#D44D5C] mb-2">{(teamInvitation as any)?.business?.name || 'the Team!'}</p>
              </>
            )}
          </div>

          <div className="px-6 pb-6 pt-2 flex flex-col gap-3">
            {accepted ? (
              <IonButton expand="block" fill="solid" color="primary" onClick={handleContinue}>
                Continue
              </IonButton>
            ) : (
              <>
                <IonButton expand="block" fill="solid" color="primary" onClick={handleAccept}>
                  Accept
                </IonButton>
                <IonButton expand="block" fill="outline" color="primary" onClick={handleReject}>
                  Reject
                </IonButton>
              </>
            )}
          </div>
        </div>
      </IonModal>
    </IonPage>
  );
};
